/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import BetManager from '../Playbar/BetManager';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ObservableDoubleComponent from '../Utility/Component/Observable/ObservableDoubleComponent';
import Decorators from '../Utility/Decorators';
import Observable from '../Utility/Observable/Observable';
import Currency, { CurrencyType } from './Currency';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class ObservableCurrencyComponent extends ObservableDoubleComponent
{
    /** BetManager reference for setting [[CurrencyType]]. */
    @property(InjectableComponent)
    protected betManagerRef: InjectableComponent<BetManager> = new InjectableComponent<BetManager>();

    /** Underlying currency object. */
    protected currency: Currency = null;
    /** Accessor to main currency value. */
    public get Currency(): Currency { return this.currency; }

    /** Observable [[CurrencyType]] for updating displays. */
    protected currentTypeObservable: Observable<CurrencyType> = new Observable<CurrencyType>(CurrencyType.MC);
    /** Public setting to change convert to [[CurrencyType]]. */
    public set CurrentType(value: CurrencyType) { this.currentTypeObservable.Value = value; }

    /** @hidden */
    protected setContexts(): void
    {
        this.betManagerRef.SetContext(BetManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        if (!this.betManagerRef.HasValue())
        {
            this.enabled = false;
            return;
        }

        this.currency = new Currency(this.betManagerRef.Value.ActiveDenomObservable, 0);

        this.betManagerRef.Value.IsCashObservable.OnChanged(this.isCashHandler);
        this.betManagerRef.Value.ActiveDenomObservable.OnChanged(() => this.broadcastUpdate(false));

        this.currency.McValue.OnChanged(() => this.broadcastUpdate());
        this.currentTypeObservable.OnChanged(() => this.broadcastUpdate(false));
    }

    /** @hidden */
    protected onDestroy(): void
    {
        this.betManagerRef.WithValue(mgr =>
        {
            if (mgr.IsCashObservable) { mgr.IsCashObservable.OffChanged(this.isCashHandler); }
            if (mgr.ActiveDenomObservable) { mgr.ActiveDenomObservable.OffChanged(() => this.broadcastUpdate()); }
        });

        if (this.currency && this.currency.McValue)
        {
            this.currency.McValue.OffChanged(() => this.broadcastUpdate());
        }

        if (this.currentTypeObservable)
        {
            this.currentTypeObservable.OffChanged(() => this.broadcastUpdate());
        }
    }

    /** Override to get value based on [[CurrencyType]]. */
    public GetValue(): number
    {
        if (!this.currency) { return 0; }
        return this.currency.GetValue(this.currentTypeObservable.Value);
    }

    /** Override to set value based on [[CurrencyType]] */
    public SetValue(value: number, type: CurrencyType = CurrencyType.MC): ObservableCurrencyComponent
    {
        if (this.IsReadOnlyObservable.Value || !this.currency) { return; }

        this.currency.SetValue(value, type);
        super.SetValue(this.currency.GetValue());

        return this;
    }

    /** Handler for BetManager isCash boolean changed. */
    @Decorators.BindThis()
    private isCashHandler = (): void =>
    {
        this.currentTypeObservable.Value = this.betManagerRef.Value.IsCash
            ? CurrencyType.CASH
            : CurrencyType.CREDITS;
    }

    /** Override for adding to valid value array. */

    public AddValidValue(
        value: number,
        type: CurrencyType = CurrencyType.MC): ObservableCurrencyComponent
    {
        value = Currency.ConvertToMC(value, type, this.betManagerRef.Value.ActiveDenom);
        return super.AddValidValue(value) as ObservableCurrencyComponent;
    }
}
